import { escapeHtml, renderMarkdown } from './markdown.js';
import { HelpRegistry } from './help-registry.js';

// Shared entry-body rendering for the tier-2 concept help UI
// (docs/PIPELINE_SANDBOX_HELP_SYSTEM.md §9/§10). Used by both <hs-help-modal>
// (tier-1 per-element popups) and <hs-concept-panel> (tier-2 concept browser)
// so callouts/see_also/navigation render and behave identically in both
// surfaces. Each surface owns its own click delegation for
// `[data-help-nav-key]` — this module only produces markup.

const CALLOUT_LABEL = { info: 'Info', warning: 'Warning', critical: 'Critical' };

// Shared stylesheet text — adopted by both consumers alongside their own
// layout-specific styles. Shadow DOM scoping means identical class names in
// two different components never collide, so no prefixing games needed.
export const HELP_ENTRY_STYLES = `
.he-callouts { margin-bottom: 12px; display: flex; flex-direction: column; gap: 6px; }
.he-callout { padding: 8px 10px; border-radius: 6px; font-size: 12.5px; line-height: 1.45; }
.he-callout-label { font-weight: 700; text-transform: uppercase; font-size: 10px; letter-spacing: 0.4px; margin-right: 6px; }
.he-callout-info { background: var(--bg-secondary, #eef4fb); border: 1px solid var(--border-color, #cfe0f2); color: var(--text-primary, #234); }
.he-callout-warning { background: var(--warning-bg, #fff6df); border: 1px solid var(--warning-border, #f0c96a); color: var(--warning-fg, #6b4e00); }
.he-callout-critical { background: #fde7e7; border: 1px solid #e07a7a; color: #7a1f1f; font-weight: 600; }
:host(.hs-theme-dark) .he-callout-critical, .he-callout-critical.hs-theme-dark { background: #3a1f1f; color: #ff9d9d; border-color: #7a3a3a; }

.he-section { margin-bottom: 14px; }
.he-section:last-child { margin-bottom: 0; }
.he-section-title { font-size: 11px; text-transform: uppercase; letter-spacing: 0.4px; color: var(--text-secondary, #888); margin-bottom: 6px; }

.he-option { margin-bottom: 8px; padding-left: 10px; border-left: 3px solid var(--border-color, #e0e0e0); }
.he-option-name { font-weight: 600; font-family: monospace; font-size: 12px; margin-bottom: 2px; }

.he-example { margin-bottom: 10px; }
.he-example-label { font-weight: 600; font-size: 12px; margin-bottom: 4px; }
.he-example pre { background: var(--bg-secondary, #f5f5f5); padding: 8px; border-radius: 4px; font-size: 11px; overflow: auto; margin: 0 0 4px; }

.he-pitfall { margin-bottom: 6px; }

.he-seealso-links { display: flex; flex-wrap: wrap; gap: 6px; }
.he-seealso-link {
    all: unset;
    cursor: pointer;
    font-size: 11px;
    font-family: monospace;
    padding: 3px 8px;
    border-radius: 10px;
    background: var(--bg-secondary, #eef2f7);
    color: var(--accent-color, #0066cc);
    border: 1px solid var(--border-color, #dbe3ec);
}
.he-seealso-link:hover { background: var(--accent-color, #0066cc); color: #fff; }

.he-draft-badge {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-weight: 700;
    color: var(--text-secondary, #888);
    background: var(--bg-secondary, #eee);
    border: 1px solid var(--border-color, #ddd);
    border-radius: 4px;
    padding: 1px 6px;
}

p { margin: 0 0 8px; }
p:last-child { margin-bottom: 0; }
ul { margin: 0 0 8px 18px; padding: 0; }
code { background: var(--bg-secondary, #f0f0f0); padding: 1px 4px; border-radius: 3px; font-size: 12px; }
`;

export function renderDraftBadge(entry) {
    return entry && entry.reviewed_by == null
        ? '<span class="he-draft-badge" title="Not yet human-reviewed">Draft</span>'
        : '';
}

export function renderCallouts(callouts) {
    if (!callouts?.length) return '';
    const items = callouts.map(c => {
        const sev = ['info', 'warning', 'critical'].includes(c.severity) ? c.severity : 'info';
        return `<div class="he-callout he-callout-${sev}"><span class="he-callout-label">${CALLOUT_LABEL[sev]}</span>${renderMarkdown(c.md || '')}</div>`;
    }).join('');
    return `<div class="he-callouts">${items}</div>`;
}

// A missing/unresolvable see_also key is skipped silently rather than
// rendered as a broken link (backend has a link-integrity test, but the
// frontend stays defensive regardless).
export function renderSeeAlso(seeAlso) {
    if (!seeAlso?.length) return '';
    const links = seeAlso
        .filter(k => HelpRegistry.getEntry(k))
        .map(k => `<button type="button" class="he-seealso-link" data-help-nav-key="${escapeHtml(k)}">${escapeHtml(k)}</button>`)
        .join('');
    if (!links) return '';
    return `<div class="he-section he-seealso"><div class="he-section-title">See also</div><div class="he-seealso-links">${links}</div></div>`;
}

// Full body for an entry's `long` content: callouts first (must be
// impossible to skim past), then summary/when_to_use/options/examples/
// pitfalls, then navigable see_also links.
export function renderEntryBody(entry) {
    const long = entry?.long;
    if (!long) return '<div class="he-section">No additional detail.</div>';

    const sections = [renderCallouts(long.callouts)];
    if (long.summary_md) {
        sections.push(`<div class="he-section">${renderMarkdown(long.summary_md)}</div>`);
    }
    if (long.when_to_use) {
        sections.push(`<div class="he-section"><div class="he-section-title">When to use</div>${renderMarkdown(long.when_to_use)}</div>`);
    }
    if (long.options?.length) {
        const opts = long.options.map(o => `<div class="he-option">
            <div class="he-option-name">${escapeHtml(o.name)}</div>
            ${renderMarkdown(o.explanation_md || '')}
            ${o.tradeoffs_md ? renderMarkdown(o.tradeoffs_md) : ''}
        </div>`).join('');
        sections.push(`<div class="he-section"><div class="he-section-title">Options</div>${opts}</div>`);
    }
    if (long.examples?.length) {
        const examples = long.examples.map(e => `<div class="he-example">
            <div class="he-example-label">${escapeHtml(e.label || '')}</div>
            <pre>${escapeHtml(JSON.stringify(e.config, null, 2))}</pre>
            ${e.note_md ? renderMarkdown(e.note_md) : ''}
        </div>`).join('');
        sections.push(`<div class="he-section"><div class="he-section-title">Examples</div>${examples}</div>`);
    }
    if (long.pitfalls_md?.length) {
        const pitfalls = long.pitfalls_md.map(p => `<div class="he-pitfall">⚠️ ${renderMarkdown(p)}</div>`).join('');
        sections.push(`<div class="he-section"><div class="he-section-title">Pitfalls</div>${pitfalls}</div>`);
    }
    sections.push(renderSeeAlso(long.see_also));
    return sections.filter(Boolean).join('');
}
